import axiosInstance from '@/utils/axiosInstance';
import { API_URL, USER_ROLES } from '@/constant/constant';

const getAuthHeaders = (vendorId) => ({
  sessionToken: localStorage.getItem('sessionToken'),
  'X-USER-ROLE': USER_ROLES.VENDOR,
  'X-Vendor-Id': vendorId,
});

/**
 * Fetch all coupons for the current vendor
 * @param {string} vendorId - The vendor ID
 * @returns {Promise<Array>} Array of coupons
 */
export const fetchVendorCoupons = async (vendorId) => {
  try {
    const response = await axiosInstance.get(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/coupons?vendorId=${vendorId}`,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data || [];
  } catch (error) {
    console.error('Error fetching coupons:', error);
    throw error;
  }
};

/**
 * Create a new coupon for the vendor
 * @param {string} vendorId - The vendor ID
 * @param {Object} payload - Coupon data
 * @returns {Promise<Object>} Created coupon data
 */
export const createCoupon = async (vendorId, payload) => {
  try {
    const payloadWithVendor = {
      ...payload,
      vendorId: vendorId,
    };
    const response = await axiosInstance.post(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/coupon?vendorId=${vendorId}`,
      payloadWithVendor,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating coupon:', error);
    throw error;
  }
};

/**
 * Update an existing coupon
 * @param {string} couponId - The coupon ID
 * @param {Object} payload - Updated coupon data
 * @returns {Promise<Object>} Updated coupon data
 */
export const updateCoupon = async (couponId, payload) => {
  try {
    const vendorId = localStorage.getItem('vendorId');
    const response = await axiosInstance.put(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/coupon/${couponId}`,
      payload,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating coupon:', error);
    throw error;
  }
};

/**
 * Delete/deactivate a coupon
 * @param {string} couponId - The coupon ID
 * @returns {Promise<Object>} Response data
 */
export const deleteCoupon = async (couponId) => {
  try {
    const vendorId = localStorage.getItem('vendorId');
    const response = await axiosInstance.delete(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/coupon/${couponId}`,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting coupon:', error);
    throw error;
  }
};

/**
 * Get detailed analytics for a coupon
 * @param {string} couponId - The coupon ID
 * @returns {Promise<Object>} Coupon analytics data
 */
export const getCouponAnalytics = async (couponId) => {
  try {
    const vendorId = localStorage.getItem('vendorId');
    const response = await axiosInstance.get(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/coupon/${couponId}/analytics`,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching coupon analytics:', error);
    throw error;
  }
};
